import React from "react";
import { useGame, useGameContracts, useMyGameEditions, usePublisherAndContacts } from "@seabrookstudios/pitch2table";
import { useRoyaltyIdFromRoute } from "../games/WithGameIdFromRoute";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import { Path } from "../Path";
import { Link } from "react-router-dom";
import { royaltyToYear, RoyaltyTypeLabels } from "@seabrookstudios/pitch2table-core";
import { RoyaltyStatusIcon } from "./RoyaltyStatusIcon";
import { RoyaltyAmount } from "./RoyaltyAmount";
import { RoyaltyDate } from "./RoyaltyDate";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Skeleton from "@mui/material/Skeleton";
import { CoverSquare } from "../games/CoverImages";
import { useUserCurrencyFormatter } from "../games/royalties/currency-formatting";
import { RoyaltyTypeIcons } from "./model";
import { buildRoyaltySecondaryTextWithUnits } from "./date-utils";
import { PublisherLogoSquare2 } from "../publisher/PublisherLogo";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp} props.item
 */
export const ListEntry = ({ item }) => {
  const { data: game } = useGame(item.gameId);
  const edition = useGameEdition(item.gameId, item.gameEditionId);
  const royaltyId = useRoyaltyIdFromRoute();

  const selected = royaltyId === item.id;

  return (
    <ListItemButton alignItems="flex-start" selected={selected} component={Link} to={Path.game(item.gameId).royalty(item.id)}>
      <RoyaltyStatusIcon item={item} />
      <ListItemText
        primary={<RoyaltyAmount item={item} />}
        secondary={buildRoyaltySecondaryTextWithUnits(
          item.units,
          RoyaltyTypeLabels[item.type],
          game?.name,
          edition?.name
        )}
      />
      <RoyaltyDate item={item} />
    </ListItemButton>
  );
};

/**
 * List item for a game's royalties list which includes linked contract name and units.
 * (Moved from `src/games/royalties/ListAndDetail.js` so it can be reused.)
 *
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp} props.item
 */
export const GameRoyaltiesListEntry = ({ gameId, item }) => {
  const edition = useGameEdition(gameId, item.gameEditionId);

  const royaltyId = useRoyaltyIdFromRoute();

  const selected = royaltyId === item.id;

  return (
    <ListItemButton alignItems="flex-start" selected={selected} component={Link} to={Path.game(gameId).royalty(item.id)}>
      <RoyaltyStatusIcon item={item} />
      <ListItemText
        primary={<RoyaltyAmount item={item} />}
        secondary={buildRoyaltySecondaryTextWithUnits(item.units, RoyaltyTypeLabels[item.type], "", edition?.name)}
      />
      <RoyaltyDate item={item} />
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp} props.item
 */
export const ListEntryWithGameInfo = ({ item }) => {
  const { data: game } = useGame(item.gameId);
  const edition = useGameEdition(item.gameId, item.gameEditionId);

  const royaltyId = useRoyaltyIdFromRoute();

  const selected = royaltyId === item.id;
  const year = royaltyToYear(item);

  return (
    <ListItemButton alignItems="flex-start" selected={selected} component={Link} to={Path.royalties().byYear(year).royalty(item.id)}>
      <ListItemAvatar>
        {game ? <CoverSquare game={game} width={48} height={48} /> : <Skeleton width={48} height={48} />}
      </ListItemAvatar>
      <ListItemText
        primary={<RoyaltyAmount item={item} />}
        secondary={buildRoyaltySecondaryTextWithUnits(
          item.units,
          RoyaltyTypeLabels[item.type],
          game?.name,
          edition?.name
        )}
      />
      <RoyaltyDate item={item} />
    </ListItemButton>
  );
};

/**
 * @param {import("@seabrookstudios/types").GameId} gameId
 * @param {import("@seabrookstudios/pitch2table-core").GameEditionId|undefined} gameEditionId
 * @returns {import("@seabrookstudios/pitch2table-core").GameEdition|undefined}
 */
const useGameEdition = (gameId, gameEditionId) => {
  const { data: editions = [] } = useMyGameEditions(gameId);

  if (!gameEditionId) {
    return undefined;
  }

  return editions.find((e) => e.id === gameEditionId);
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp} props.item
 */
export const ListEntryByType = ({ item }) => {
  const { data: game } = useGame(item.gameId);
  const edition = useGameEdition(item.gameId, item.gameEditionId);

  const royaltyId = useRoyaltyIdFromRoute();

  const selected = royaltyId === item.id;

  return (
    <ListItemButton alignItems="flex-start" selected={selected} component={Link} to={Path.royalties().byType(item.type).royalty(item.id)}>
      <ListItemAvatar>
        {game ? <CoverSquare game={game} width={48} height={48} /> : <Skeleton width={48} height={48} />}
      </ListItemAvatar>
      <ListItemText
        primary={<RoyaltyAmount item={item} />}
        secondary={buildRoyaltySecondaryTextWithUnits(
          item.units,
          RoyaltyTypeLabels[item.type],
          game?.name,
          edition?.name
        )}
      />
      <RoyaltyDate item={item} />
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp} props.item
 */
export const ListEntryByPublisher = ({ item }) => {
  const { data: game } = useGame(item.gameId);
  const edition = useGameEdition(item.gameId, item.gameEditionId);

  const royaltyId = useRoyaltyIdFromRoute();

  const selected = royaltyId === item.id;

  const publisherKey =
    !item.publisherId || item.publisherId === "unset" ? "unlinked" : item.publisherId;

  return (
    <ListItemButton
      alignItems="flex-start"
      selected={selected}
      component={Link}
      to={Path.royalties().byPublisher(publisherKey).royalty(item.id)}
    >
      <ListItemAvatar>
        {game ? <CoverSquare game={game} width={48} height={48} /> : <Skeleton width={48} height={48} />}
      </ListItemAvatar>
      <ListItemText
        primary={<RoyaltyAmount item={item} />}
        secondary={buildRoyaltySecondaryTextWithUnits(
          item.units,
          RoyaltyTypeLabels[item.type],
          game?.name,
          edition?.name
        )}
      />
      <RoyaltyDate item={item} />
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]} props.item
 */
export const RoyaltiesByGameListEntry = ({ item }) => {
  const gameId = item[0].gameId;
  const { data: game } = useGame(gameId);
  const formatUserCurrency = useUserCurrencyFormatter();

  const total = item.reduce((t, royalty) => t + royalty.convertedAmount, 0);
  const paid = item.filter((r) => r.paid).reduce((t, royalty) => t + royalty.convertedAmount, 0);

  return (
    <ListItemButton alignItems="flex-start" selected={false} component={Link} to={Path.game(gameId).royalties()}>
      <ListItemAvatar>
        {game ? <CoverSquare game={game} width={48} height={48} /> : <Skeleton width={48} height={48} />}
      </ListItemAvatar>
      <ListItemText
        primary={game ? game.name : <Skeleton />}
        secondary={`${formatUserCurrency(paid)} ${
          total !== paid ? `(${formatUserCurrency(total)} including unpaid)` : ""
        }`}
      />
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]} props.item
 */
export const RoyaltiesByYearListEntry = ({ item }) => {
  const formatUserCurrency = useUserCurrencyFormatter();

  const year = royaltyToYear(item[0]);

  const total = item.reduce((t, royalty) => t + royalty.convertedAmount, 0);
  const paid = item.filter((r) => r.paid).reduce((t, royalty) => t + royalty.convertedAmount, 0);

  return (
    <ListItemButton alignItems="flex-start" selected={false} component={Link} to={Path.royalties().byYear(year).all()}>
      <ListItemAvatar>{year}</ListItemAvatar>
      <ListItemText
        primary={`${formatUserCurrency(paid)} ${
          total !== paid ? `(${formatUserCurrency(total)} including unpaid)` : ""
        }`}
        secondary={item.length === 1 ? "1 payment" : `${item.length} payments`}
      />
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]} props.item
 */
export const RoyaltiesByTypeListEntry = ({ item }) => {
  const formatUserCurrency = useUserCurrencyFormatter();

  const { type } = item[0];

  const total = item.reduce((t, royalty) => t + royalty.convertedAmount, 0);
  const paid = item.filter((r) => r.paid).reduce((t, royalty) => t + royalty.convertedAmount, 0);

  const Icon = RoyaltyTypeIcons[type];

  return (
    <ListItemButton alignItems="flex-start" selected={false} component={Link} to={Path.royalties().byType(type).all()}>
      <ListItemAvatar>
        <Icon />
      </ListItemAvatar>
      <ListItemText
        primary={RoyaltyTypeLabels[type]}
        secondary={`${formatUserCurrency(paid)} ${
          total !== paid ? `(${formatUserCurrency(total)} including unpaid)` : ""
        }`}
      />
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").RoyaltyEventForApp[]} props.item
 */
export const RoyaltiesByPublisherListEntry = ({ item }) => {
  const publisherId = item[0].publisherId;
  const { data: pubAndContact = [] } = usePublisherAndContacts(
    /** @type {*} */ (publisherId === "unset" ? undefined : publisherId)
  );
  const [publisher] = pubAndContact;
  const formatUserCurrency = useUserCurrencyFormatter();

  const publisherKey = !publisherId || publisherId === "unset" ? "unlinked" : publisherId;
  const to = Path.royalties().byPublisher(publisherKey).all();

  const total = item.reduce((t, royalty) => t + royalty.convertedAmount, 0);
  const paid = item.filter((r) => r.paid).reduce((t, royalty) => t + royalty.convertedAmount, 0);

  if (!publisherId) {
    return (
      <ListItemButton alignItems="flex-start" selected={false} component={Link} to={to}>
        <ListItemAvatar></ListItemAvatar>
        <ListItemText
          primary="No Publisher Linked"
          secondary={`${formatUserCurrency(paid)} ${
            total !== paid ? `(${formatUserCurrency(total)} including unpaid)` : ""
          }`}
        />
      </ListItemButton>
    );
  }

  return (
    <ListItemButton alignItems="flex-start" selected={false} component={Link} to={to}>
      <ListItemAvatar>
        {publisher ? <PublisherLogoSquare2 publisher={publisher} /> : <Skeleton width={32} height={32} />}
      </ListItemAvatar>
      <ListItemText
        primary={publisher ? publisher.name : <Skeleton />}
        secondary={`${formatUserCurrency(paid)} ${
          total !== paid ? `(${formatUserCurrency(total)} including unpaid)` : ""
        }`}
      />
    </ListItemButton>
  );
};
